<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { createAuthAxios } from '../../../../../services/auth';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const authAxios = createAuthAxios();
const localState = ref(null);
const checkingState = ref(false);
const initializingServer = ref(false);
const refreshTimeout = ref(null);

const accountId = computed(() => window.location.pathname.split('/')[3]);

const isQuePasaInbox = computed(() => {
  return window.quepasa?.service?.inbox?.isQuePasa(props.inbox) || false;
});

const reactiveState = computed(() => {
  if (!props.inbox?.id || !window.quepasa?.inboxStates) {
    return null;
  }

  return window.quepasa.inboxStates[props.inbox.id] || null;
});

const currentState = computed(() => {
  if (initializingServer.value) {
    return 'Preparing';
  }

  return reactiveState.value || localState.value;
});

const shouldShow = computed(() => isQuePasaInbox.value && !!currentState.value);

const isInitializable = computed(
  () => currentState.value === 'UnPrepared' && !initializingServer.value
);

const requiresQrOrPairing = computed(() => currentState.value === 'UnVerified');

const hasAction = computed(
  () => isInitializable.value || requiresQrOrPairing.value
);

const labelText = computed(() => {
  const state = currentState.value;

  if (state === 'UnPrepared')
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.UNPREPARED');
  if (state === 'Preparing')
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.PREPARING');
  if (state === 'UnVerified')
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.UNVERIFIED');
  if (state === 'Disconnected')
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.DISCONNECTED');
  if (state === 'Ready')
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.READY');
  if (state === 'Stopped')
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.STOPPED');
  if (state === 'Starting')
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.STARTING');
  if (state === 'Connecting')
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.CONNECTING');
  if (state === 'Restarting')
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.RESTARTING');
  if (checkingState.value)
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.CHECKING');

  return state || t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.STATES.CHECKING');
});

const getStateColor = state => {
  const statusColors = window.quepasa?.config?.STATUS_COLORS;

  return (
    window.quepasa?.getStateColor?.(state) ||
    statusColors?.[state] ||
    Reflect.get(statusColors || {}, '__default') ||
    '#9ca3af'
  );
};

const toRgb = color => {
  if (!color || typeof color !== 'string') {
    return null;
  }

  const normalized = color.trim();

  if (/^#[0-9a-f]{3}$/i.test(normalized)) {
    return normalized
      .slice(1)
      .split('')
      .map(char => parseInt(char + char, 16))
      .join(', ');
  }

  if (/^#[0-9a-f]{6}$/i.test(normalized)) {
    return [
      parseInt(normalized.slice(1, 3), 16),
      parseInt(normalized.slice(3, 5), 16),
      parseInt(normalized.slice(5, 7), 16),
    ].join(', ');
  }

  const rgbMatch = normalized.match(/^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);

  if (rgbMatch) {
    return rgbMatch.slice(1, 4).join(', ');
  }

  return null;
};

const asRgba = (color, alpha) => {
  const rgb = toRgb(color);
  return rgb ? `rgba(${rgb}, ${alpha})` : color;
};

const stateColor = computed(() => getStateColor(currentState.value));

const labelClass = computed(() => {
  return hasAction.value ? 'quepasa-status-label--clickable' : 'cursor-default';
});

const labelStyle = computed(() => {
  const color = stateColor.value;

  return {
    color,
    borderColor: asRgba(color, 0.24),
    backgroundColor: asRgba(color, 0.1),
    '--quepasa-status-color': color,
    '--quepasa-status-hover-bg': asRgba(color, 0.16),
    '--quepasa-status-hover-border': asRgba(color, 0.32),
    '--quepasa-status-focus': asRgba(color, 0.28),
  };
});

const stateDotStyle = computed(() => {
  const color = stateColor.value;

  return {
    backgroundColor: color,
    boxShadow: `0 0 0 3px ${asRgba(color, 0.18)}`,
  };
});

const actionTooltip = computed(() => {
  if (isInitializable.value) {
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.TOOLTIPS.INITIALIZE');
  }

  if (requiresQrOrPairing.value) {
    return t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.TOOLTIPS.UNVERIFIED');
  }

  return null;
});

const syncState = state => {
  if (!state) {
    return;
  }

  localState.value = state;

  if (window.quepasa?.inboxStates && props.inbox?.id) {
    window.quepasa.inboxStates[props.inbox.id] = state;
  }
};

const refreshState = async () => {
  if (!isQuePasaInbox.value || !props.inbox?.id || !accountId.value) {
    return;
  }

  try {
    checkingState.value = true;

    const statusResponse = await authAxios.get(
      `/api/v1/accounts/${accountId.value}/quepasa/server/status`,
      {
        params: { inbox_id: props.inbox.id },
        validateStatus: status =>
          (status >= 200 && status < 300) || status === 204,
      }
    );

    if (statusResponse.status === 204) {
      syncState('UnPrepared');
      return;
    }

    const healthResponse = await authAxios.get(
      `/api/v1/accounts/${accountId.value}/quepasa/server/health`,
      { params: { inbox_id: props.inbox.id } }
    );

    syncState(healthResponse.data?.state || 'Unknown');
  } catch (error) {
    const responseStatus = error.response?.status;
    const quepasaStatus = error.response?.data?.quepasa_status;
    const isUnprepared =
      (responseStatus === 404 && quepasaStatus === 204) ||
      responseStatus === 204;

    if (isUnprepared) {
      syncState('UnPrepared');
      return;
    }

    localState.value = reactiveState.value || 'Unknown';
  } finally {
    checkingState.value = false;
    if (currentState.value !== 'UnPrepared') {
      initializingServer.value = false;
    }
  }
};

const scheduleRefresh = (delayMs = 1200) => {
  if (refreshTimeout.value) {
    clearTimeout(refreshTimeout.value);
  }

  refreshTimeout.value = setTimeout(() => {
    refreshTimeout.value = null;
    refreshState();
  }, delayMs);
};

const initializeServer = async () => {
  if (!isInitializable.value || !props.inbox?.id || !accountId.value) {
    return;
  }

  try {
    initializingServer.value = true;
    localState.value = 'Preparing';

    await authAxios.post(
      `/api/v1/accounts/${accountId.value}/quepasa/server/info`,
      {
        inbox_id: props.inbox.id,
      }
    );

    useAlert(t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.ALERTS.INITIALIZING'));
    scheduleRefresh();
  } catch (error) {
    initializingServer.value = false;
    syncState('UnPrepared');
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('QUEPASA_INBOX.SETTINGS.STATUS_BADGE.ALERTS.INITIALIZE_FAILED')
    );
  }
};

const handleClick = () => {
  if (isInitializable.value) {
    initializeServer();
    return;
  }

  if (requiresQrOrPairing.value) {
    window.dispatchEvent(
      new CustomEvent('quepasa:open-settings-tab', {
        detail: {
          inboxId: String(props.inbox?.id),
          tab: 'quepasa',
        },
      })
    );
  }
};

const handleInboxStateChanged = event => {
  const payload = event?.detail || {};
  if (String(payload?.inbox_id) !== String(props.inbox?.id)) {
    return;
  }

  if (payload?.current_state) {
    syncState(payload.current_state);
  }

  if (payload?.current_state && payload.current_state !== 'UnPrepared') {
    initializingServer.value = false;
  }
};

watch(
  () => props.inbox?.id,
  () => {
    localState.value = null;
    initializingServer.value = false;
    if (isQuePasaInbox.value) {
      refreshState();
    }
  },
  { immediate: true }
);

watch(reactiveState, newState => {
  if (newState) {
    localState.value = newState;
  }
});

onMounted(() => {
  window.addEventListener(
    'quepasa:inbox-state-changed',
    handleInboxStateChanged
  );
  refreshState();
});

onBeforeUnmount(() => {
  window.removeEventListener(
    'quepasa:inbox-state-changed',
    handleInboxStateChanged
  );

  if (refreshTimeout.value) {
    clearTimeout(refreshTimeout.value);
  }
});
</script>

<template>
  <div>
    <button
      v-if="shouldShow && hasAction"
      v-tooltip.bottom="actionTooltip"
      type="button"
      class="quepasa-status-label inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium leading-none transition-colors"
      :class="labelClass"
      :style="labelStyle"
      @click="handleClick"
    >
      <span
        class="quepasa-status-dot inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        :style="stateDotStyle"
      />
      <span>{{ labelText }}</span>
    </button>
    <span
      v-else-if="shouldShow"
      class="quepasa-status-label inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium leading-none"
      :class="labelClass"
      :style="labelStyle"
    >
      <span
        class="quepasa-status-dot inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        :style="stateDotStyle"
      />
      <span>{{ labelText }}</span>
    </span>
  </div>
</template>

<style scoped>
.quepasa-status-label {
  border-style: solid;
}

.quepasa-status-label--clickable:hover {
  border-color: var(--quepasa-status-hover-border);
  background-color: var(--quepasa-status-hover-bg);
}

.quepasa-status-label--clickable:focus-visible {
  outline: 2px solid var(--quepasa-status-focus);
  outline-offset: 2px;
}

.quepasa-status-dot {
  flex-shrink: 0;
}
</style>
